// @ts-strict-ignore
import { AnalyticEventAction } from 'analytics';
import { trackMultiSelectAnalyticsEvent } from 'analytics/events/multi-select';
import { ErrorWithUi } from 'errors';

import { orderBy as orderByFp, flow, map, filter, reverse } from 'lodash/fp';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { computedFn } from 'mobx-utils';

import { RootStore } from 'mobx/stores';
import { DataMap } from 'mobx/stores/DataMap';

import { CallsFetcher } from 'fetchers/CallsFetcher';

import CommentsFetcher from 'fetchers/CommentsFetcher';
import TicketsFetcher, {
  ICreateTicketResult,
  IOperatorTicketsCreateRequest,
  IOperatorTicketUpdateRequest,
  ITicketCommentCreateRequest,
  TicketCheckStatusResponse,
  TicketResolutionResponse
} from 'fetchers/TicketsFetcher';

import { handleErrorSilently } from 'services/errorHandlingService';

import { RequestConfig } from 'utils/http.utils';
import { extractTicketQueryFromFilters, mergeWithQueryDefaults } from 'utils/serverFiltersUtils';
import { ExtendedTicket } from 'utils/TicketClusteringUtils';

import {
  getTicketResolutionResultMessages,
  TICKET_MISSING_TYPE,
  TicketResolveResponseType
} from 'utils/TicketMessageUtils';

import { showToast } from 'utils/UserMessageUtils';

import Patient from 'models/Patient';
import QuestionnaireAnswer from 'models/QuestionnaireAnswer';
import Ticket, { TicketClass, TicketStatus } from 'models/Ticket';

import { TicketsQueryRequestParams } from 'views/Filters/filters.types';

export enum TicketTypes {
  ticket = 'ticket',
  task = 'task'
}

export default class TicketsStore {
  @observable
  ticketsMap: DataMap<Ticket>;

  @observable
  ticketsBulkActionSet = observable.set<number>();

  @observable
  messagingFocusMap = observable.map<number, boolean>();

  @observable
  initialTicketsReceived: boolean = false;

  @observable
  lastRetrievedTimestamp: number = null;

  @observable
  patientTicketsFetched: number;

  rootStore: RootStore;

  refreshInterval: any;

  refreshSinglePatientTicketsInterval: NodeJS.Timeout;

  constructor(rootStore: RootStore) {
    makeObservable(this);
    this.rootStore = rootStore;
    const { stores } = this.rootStore;
    this.ticketsMap = new DataMap<Ticket>({
      fallbacks: new Set([
        {
          token: 'TicketsStore.ticketsMap_ticketTypes',
          isDataExist: (ticket: Ticket) => stores.ticketTypesStore.isTicketTypesAvailable(ticket),
          fetchMissingData: () => stores.ticketTypesStore.fetchOperatorTicketTypes(),
          onDataNotFound: (ticket: Ticket) => {
            throw new ErrorWithUi(TICKET_MISSING_TYPE(ticket.id));
          }
        }
      ])
    });
  }

  @computed
  get tickets(): Ticket[] {
    return this.ticketsMap.items.filter((ticket: Ticket) => ticket.class !== TicketClass.task);
  }

  @computed
  get openTickets() {
    return this.tickets.filter((ticket) => ticket.status !== TicketStatus.RESOLVED);
  }

  handleTicketsResponse = (tickets: Ticket[], requestTimestamp: number) => {
    runInAction(() => {
      if (!this.initialTicketsReceived) {
        this.initialTicketsReceived = true;
      }
      this.lastRetrievedTimestamp ? this.addTickets(tickets) : this.setTickets(tickets);
      this.lastRetrievedTimestamp = requestTimestamp;
    });
  };

  ticketCheckStatus = (ticketId: number): Promise<TicketCheckStatusResponse> => {
    return TicketsFetcher.ticketCheckStatus(ticketId);
  };

  async fetchTicketsForPatient(patientId: number, clearTickets = true) {
    const { ticketFiltersStore } = this.rootStore.stores;
    const queryFromFilters = extractTicketQueryFromFilters(ticketFiltersStore.filters);

    const queryWithDefaults = mergeWithQueryDefaults(
      queryFromFilters,
      ticketFiltersStore.defaultTicketsQueryValues
    );

    const finalQuery = {
      ...queryWithDefaults,
      reportTypes: ticketFiltersStore.reportTypesFilterValue,
      ticketStatuses: Ticket.TICKET_ACTIVE_STATUSES,
      includeAssignToMe: true,
      patientId
    };

    const tickets = await TicketsFetcher.searchTickets(finalQuery);
    runInAction(() => {
      clearTickets ? this.setTickets(tickets) : this.addTickets(tickets);
      this.patientTicketsFetched = patientId;
    });
  }

  @action
  fetchTicketsForSinglePatientPeriodically(patientId: number) {
    if (!this.refreshSinglePatientTicketsInterval) {
      const { settingsStore } = this.rootStore.stores;
      const { singlePatientRetrievalInterval } = settingsStore.institutionSettings;
      this.refreshSinglePatientTicketsInterval = setInterval(() => {
        this.fetchTicketsForPatient(patientId).catch(handleErrorSilently);
      }, singlePatientRetrievalInterval);
    }
    return this.fetchTicketsForPatient(patientId);
  }

  searchTickets = async (query: TicketsQueryRequestParams, cancellationKey?: string) => {
    const tickets = await TicketsFetcher.searchTickets(query, cancellationKey);
    this.addTickets(tickets);
    return tickets;
  };

  searchTicketsForResolvedTicketsTab = (patientId: number) => {
    return this.searchTickets({
      patientId,
      ticketStatuses: [TicketStatus.RESOLVED]
    });
  };

  searchTicketsForCreate = async (patientId: number) => {
    const tickets = await this.searchTickets({
      patientId,
      ticketStatuses: Ticket.TICKET_ACTIVE_STATUSES
    });

    this.setTickets(tickets);
  };

  getPatientTickets = computedFn((patient: Patient): Ticket[] => {
    return this.tickets.filter((ticket) => patient.id === ticket.patientId);
  });

  getPatientOpenTickets = computedFn((patient?: Patient): Ticket[] => {
    if (!patient) {
      return [];
    }
    return this.openTickets.filter((ticket) => {
      if (patient.id === ticket.patientId) {
        return (
          ticket.isOperatorTicket ||
          ticket.isCallbackRequestTicket ||
          patient.hasUnacknowledgedReports(ticket)
        );
      }
      return false;
    });
  });

  patientHasOpenTickets = computedFn((patient: Patient): boolean => {
    return this.getPatientOpenTickets(patient).length > 0;
  });

  getPatientSymptomOperatorTickets = computedFn(
    (patient: Patient): Ticket[] =>
      this.getPatientOpenTickets(patient).filter((ticket) => ticket.isSymptomOperatorTicket) || []
  );

  getPatientResolvedTickets = computedFn((patient: Patient): Ticket[] =>
    this.getPatientTickets(patient).filter((ticket) => ticket.isResolved)
  );

  getPatientSortedResolvedTickets = computedFn((patient: Patient): Ticket[] => {
    return orderByFp<Ticket>(['resolvedDate'], ['asc'], this.getPatientResolvedTickets(patient));
  });

  /**
   * Clustering sorted reports to sorted tickets (ascending date order)
   * Under the condition that a report creation date must be before ticket resolution
   */
  getPatientSortedResolvedTicketsWithReports = computedFn(
    (patient: Patient): ExtendedTicket[] | null => {
      const sortedResolvedTickets = this.getPatientSortedResolvedTickets(patient) || [];
      if (sortedResolvedTickets.length === 0) {
        return null;
      }

      const reports = [...patient.questionnairesAnswers];
      const resolvedTicketIds = new Set(sortedResolvedTickets.map((t) => t.id));
      const relevantReports = reports.filter((report) => resolvedTicketIds.has(report.ticketId));

      return flow(
        // Process each ticket and build extended tickets
        map((ticket: Ticket): ExtendedTicket | null => {
          if (ticket.isOperatorTicket || ticket.isCallbackRequestTicket) {
            return { ticket, reports: [] };
          }

          const ticketReports = relevantReports.filter(
            (report: QuestionnaireAnswer) => report.ticketId === ticket.id
          );
          // Remove processed reports
          relevantReports.splice(
            0,
            relevantReports.length,
            ...relevantReports.filter(
              (report: QuestionnaireAnswer) => report.ticketId !== ticket.id
            )
          );

          if (ticketReports.length) {
            return { ticket, reports: ticketReports };
          } else {
            console.warn(`ticket ${ticket.id} has no reports`);
            return null;
          }
        }),
        // Filter out null results and reverse to get last resolved ticket first
        filter(Boolean),
        reverse
      )(sortedResolvedTickets) as ExtendedTicket[];
    }
  );

  @action
  stopRefresh() {
    clearInterval(this.refreshInterval);
    this.refreshInterval = null;
  }

  @action
  stopSinglePatientRefresh() {
    clearInterval(this.refreshSinglePatientTicketsInterval);
    this.refreshSinglePatientTicketsInterval = null;
  }

  @action
  clearData() {
    this.ticketsMap.clear();
    this.initialTicketsReceived = false;
    this.patientTicketsFetched = null;
  }

  @action
  resetStore() {
    this.clearData();
    this.stopRefresh();
    this.resetTimestamp();
  }

  @action
  resetTimestamp() {
    this.lastRetrievedTimestamp = null;
  }

  @action
  clearTickets() {
    // Clearing all besides tasks
    this.tickets.forEach((ticket) => {
      this.ticketsMap.delete(ticket.id);
    });
  }

  // sync comments - temp fix for https://expain.atlassian.net/browse/EH-2444
  syncComments(tickets: Ticket[]) {
    tickets.forEach((ticket) => {
      const oldTicket = this.ticketsMap.get(ticket.id);
      ticket.comments = oldTicket?.comments;
    });
  }

  @action
  setTickets = (tickets: Ticket[]) => {
    this.syncComments(tickets);
    this.clearTickets();
    this.addTickets(tickets, false);
  };

  @action
  addTickets(tickets: Ticket[], syncComments = true) {
    if (syncComments) {
      this.syncComments(tickets);
    }
    this.ticketsMap.replaceItems(tickets);
  }

  updateTicketAssign(ticket: Ticket, doctorId: number | null) {
    const isReassign = Boolean(ticket.mainAssign);
    return TicketsFetcher.assignTicket(ticket.id, doctorId, isReassign);
  }

  async deleteTicket(ticketId: number) {
    await TicketsFetcher.deleteTicket(ticketId);
  }

  @action
  async resolveTickets(
    ticketIds: number[],
    comment: string = '',
    allowResolveLocalPatientTicket: boolean = false
  ): Promise<TicketResolutionResponse> {
    const resolveTicketsResult = await TicketsFetcher.resolveTickets(
      ticketIds,
      comment,
      allowResolveLocalPatientTicket
    );
    this.handleTicketResolutionMessages(resolveTicketsResult);
    return resolveTicketsResult;
  }

  handleTicketResolutionMessages = (response: TicketResolveResponseType) => {
    const { title, description } = getTicketResolutionResultMessages(response);
    showToast({ message: title, description });
  };

  updateTicket(id: number, data: IOperatorTicketUpdateRequest): Promise<Ticket> {
    return TicketsFetcher.updateTicket(id, data);
  }

  async createTickets(data: IOperatorTicketsCreateRequest): Promise<ICreateTicketResult> {
    return await TicketsFetcher.createTickets(data);
  }

  addTicketComment(ticket: Ticket, text: string) {
    const createTicketRequestData: ITicketCommentCreateRequest = {
      ticketId: ticket.id,
      text: text
    };

    return TicketsFetcher.createTicketComment(createTicketRequestData).then((comment) => {
      ticket.addComment(comment);
      return comment;
    });
  }

  async fetchCommentsForTicket(ticket: Ticket) {
    const comments = await CommentsFetcher.fetchCommentsByIdAndType(ticket.id);
    ticket.setComments(comments);
    return comments;
  }

  async fetchMessagesForTicket(ticket: Ticket) {
    const messages = await TicketsFetcher.getTicketMessages(ticket.id);
    ticket.setMessages(messages);
    return messages;
  }

  getTicketHistory(ticket: Ticket, requestConfig: RequestConfig) {
    return TicketsFetcher.getTicketHistory(ticket.id, requestConfig);
  }

  fetchTicketsCalls(ticketIds: number[] = []) {
    return CallsFetcher.fetchCallsForTickets(ticketIds);
  }

  @action
  resetTicketsBulkActionSet() {
    this.ticketsBulkActionSet.clear();
  }

  @action
  toggleBulkTicket(ticketId: number) {
    let action: AnalyticEventAction.Add | AnalyticEventAction.Remove;

    if (this.ticketsBulkActionSet.has(ticketId)) {
      action = AnalyticEventAction.Remove;
      this.ticketsBulkActionSet.delete(ticketId);
    } else {
      action = AnalyticEventAction.Add;
      this.ticketsBulkActionSet.add(ticketId);
    }

    trackMultiSelectAnalyticsEvent({
      action,
      value: `${this.ticketsBulkActionSet.size} selected items`
    });
  }

  @action
  focusMessagingInput(ticketId: number) {
    this.messagingFocusMap.set(ticketId, true);
  }

  @computed get ticketsBulkAction(): Ticket[] {
    return this.ticketsMap.getItemByKeys(Array.from(this.ticketsBulkActionSet));
  }

  // return patientId if all tickets in bulk have the same patientId, otherwise return null;
  @computed get ticketsBulkPatientId(): number {
    if (this.ticketsBulkAction.length > 0) {
      const { patientId } = this.ticketsBulkAction[0];
      if (this.ticketsBulkAction.every((ticket) => ticket.patientId === patientId)) {
        return patientId;
      }
    }
    return null;
  }

  @computed get nonTaskTicketIdsBulkAction(): number[] {
    return this.ticketsBulkAction.filter((ticket) => !ticket.isTask).map((ticket) => ticket.id);
  }

  @computed get taskTicketIdsBulkAction(): number[] {
    return this.ticketsBulkAction.filter((ticket) => ticket.isTask).map((ticket) => ticket.id);
  }
  @computed get bulkActionTickets(): Ticket[] {
    return this.ticketsMap.getItemByKeys(Array.from(this.ticketsBulkActionSet));
  }

  @computed get bulkActionTicketIds() {
    return Array.from(this.ticketsBulkActionSet);
  }

  @computed get bulkSelectedTicketsTicketTypes(): Set<TicketTypes> {
    const res = new Set<TicketTypes>();

    this.bulkActionTickets.forEach((ticket: Ticket) =>
      res.add(ticket.class === TicketClass.task ? TicketTypes.task : TicketTypes.ticket)
    );

    return res;
  }
}
